import { normalizeDatabase } from "./database";
import { limitForEntitlement } from "./entitlements";
import { cleanSellerListingDescription } from "./logic";
import { syncNotifications } from "./notifications";
import { makeID } from "./seed";
import type { InventoryCategory, InventoryItem, Marketplace, ReOSDatabase } from "./types";

export interface LocalBackupImportSummary {
  categories: number;
  inventoryItems: number;
  marketplaceListings: number;
  sales: number;
}

export interface LocalBackupImportSkipped {
  itemPhotos: number;
  storedAssets: number;
  marketplaceAccounts: number;
  notifications: number;
  marketplaceListings: number;
  sales: number;
}

export interface LocalBackupImportResult {
  database: ReOSDatabase;
  imported: LocalBackupImportSummary;
  skipped: LocalBackupImportSkipped;
}

export class LocalBackupImportLimitError extends Error {
  constructor(
    readonly used: number,
    readonly requested: number,
    readonly limit: number
  ) {
    super(`Import would exceed the ${limit} item limit for this workspace.`);
  }
}

export function importLocalBackupIntoWorkspace(input: {
  targetDatabase: ReOSDatabase;
  currentUserID: string;
  workspaceID: string;
  backup: unknown;
  now?: Date;
}): LocalBackupImportResult {
  const targetDatabase = normalizeDatabase(input.targetDatabase);
  const sourceDatabase = databaseFromBackup(input.backup);
  const now = input.now ?? new Date();
  const timestamp = now.toISOString();

  const targetWorkspace = targetDatabase.workspaces.find((workspace) => workspace.id === input.workspaceID);
  const targetUser = targetDatabase.users.find((user) => user.id === input.currentUserID);
  if (!targetWorkspace || !targetUser) {
    throw new Error("Create or load a workspace before importing a backup.");
  }

  const sourceWorkspaceIDs = workspaceIDsForSource(sourceDatabase);
  const sourceItems = sourceDatabase.inventoryItems.filter(
    (item) => isRecord(item) && hasText(item.id) && inSourceWorkspace(item.workspaceID, sourceWorkspaceIDs)
  );
  const currentItemCount = targetDatabase.inventoryItems.filter((item) => item.workspaceID === input.workspaceID).length;
  const itemLimit = limitForEntitlement(targetDatabase, input.workspaceID, "inventory_items", now);
  if (currentItemCount + sourceItems.length > itemLimit) {
    throw new LocalBackupImportLimitError(currentItemCount, sourceItems.length, itemLimit);
  }

  const categoryIDToPath = new Map<string, string>();
  const existingCategoryPaths = new Map(
    targetDatabase.categories
      .filter((category) => category.workspaceID === input.workspaceID)
      .map((category) => [category.path.trim().toLowerCase(), category.path])
  );
  const importedCategories: InventoryCategory[] = [];
  const ensureCategory = (pathInput: string, sourceCategory?: InventoryCategory) => {
    const path = pathInput.trim() || "Imported";
    const key = path.toLowerCase();
    const existingPath = existingCategoryPaths.get(key);
    if (existingPath) {
      return existingPath;
    }

    const category: InventoryCategory = {
      id: makeID("category"),
      workspaceID: input.workspaceID,
      name: sourceCategory?.name?.trim() || categoryNameForPath(path),
      path,
      isSystem: false,
      createdAt: sourceCategory?.createdAt || timestamp,
      updatedAt: timestamp
    };
    importedCategories.push(category);
    existingCategoryPaths.set(key, path);
    return path;
  };

  sourceDatabase.categories
    .filter((category) => isRecord(category) && hasText(category.id) && inSourceWorkspace(category.workspaceID, sourceWorkspaceIDs))
    .forEach((category) => {
      const path = category.path?.trim() || category.name?.trim() || category.id;
      categoryIDToPath.set(category.id, ensureCategory(path, category));
    });

  const usedSKUs = new Set(
    targetDatabase.inventoryItems
      .filter((item) => item.workspaceID === input.workspaceID)
      .map((item) => item.sku.trim().toLowerCase())
      .filter(Boolean)
  );
  const itemIDMap = new Map<string, string>();
  const importedItems = sourceItems.map((item, index): InventoryItem => {
    const nextID = makeID("item");
    itemIDMap.set(item.id, nextID);
    const categoryID = ensureCategory(categoryIDToPath.get(item.categoryID) ?? item.categoryID ?? "Imported");

    return {
      ...clone(item),
      id: nextID,
      workspaceID: input.workspaceID,
      categoryID,
      sku: uniqueSKU(item.sku || `IMPORTED-${index + 1}`, usedSKUs),
      createdAt: item.createdAt || timestamp,
      updatedAt: timestamp
    };
  });

  const targetAccountByMarketplace = new Map(
    targetDatabase.marketplaceAccounts
      .filter((account) => account.workspaceID === input.workspaceID)
      .map((account) => [account.marketplace, account])
  );
  const listingIDMap = new Map<string, string>();
  let skippedListings = 0;
  const importedListings = sourceDatabase.marketplaceListings.flatMap((listing) => {
    if (!isRecord(listing) || !hasText(listing.id) || !inSourceWorkspace(listing.workspaceID, sourceWorkspaceIDs)) {
      return [];
    }
    const itemID = itemIDMap.get(listing.itemID);
    const account = targetAccountByMarketplace.get(listing.marketplace as Marketplace);
    if (!itemID || !account) {
      skippedListings += 1;
      return [];
    }
    const nextID = makeID("listing");
    listingIDMap.set(listing.id, nextID);
    return [
      {
        ...clone(listing),
        id: nextID,
        workspaceID: input.workspaceID,
        itemID,
        marketplaceAccountID: account.id,
        marketplace: account.marketplace,
        description: cleanSellerListingDescription(listing.description, listing.title),
        createdAt: listing.createdAt || timestamp,
        updatedAt: timestamp
      }
    ];
  });

  let skippedSales = 0;
  const importedSales = sourceDatabase.sales.flatMap((sale) => {
    if (!isRecord(sale) || !hasText(sale.id) || !inSourceWorkspace(sale.workspaceID, sourceWorkspaceIDs)) {
      return [];
    }
    const itemID = itemIDMap.get(sale.itemID);
    if (!itemID) {
      skippedSales += 1;
      return [];
    }
    const listingID = sale.listingID ? listingIDMap.get(sale.listingID) : undefined;
    return [
      {
        ...clone(sale),
        id: makeID("sale"),
        workspaceID: input.workspaceID,
        itemID,
        listingID,
        soldAt: sale.soldAt || timestamp,
        createdAt: sale.createdAt || timestamp,
        updatedAt: timestamp
      }
    ];
  });

  const nextDatabase = syncNotifications(
    normalizeDatabase({
      ...targetDatabase,
      currentUserID: input.currentUserID,
      activeWorkspaceID: input.workspaceID,
      categories: [...targetDatabase.categories, ...importedCategories],
      inventoryItems: [...targetDatabase.inventoryItems, ...importedItems],
      marketplaceListings: [...targetDatabase.marketplaceListings, ...importedListings],
      sales: [...targetDatabase.sales, ...importedSales]
    }),
    now
  );

  return {
    database: nextDatabase,
    imported: {
      categories: importedCategories.length,
      inventoryItems: importedItems.length,
      marketplaceListings: importedListings.length,
      sales: importedSales.length
    },
    skipped: {
      itemPhotos: sourceDatabase.itemPhotos.filter((photo) => isRecord(photo) && itemIDMap.has(photo.itemID)).length,
      storedAssets: sourceDatabase.storedAssets.filter((asset) => isRecord(asset) && inSourceWorkspace(asset.workspaceID, sourceWorkspaceIDs)).length,
      marketplaceAccounts: sourceDatabase.marketplaceAccounts.filter(
        (account) => isRecord(account) && inSourceWorkspace(account.workspaceID, sourceWorkspaceIDs)
      ).length,
      notifications: sourceDatabase.notifications.filter(
        (notification) => isRecord(notification) && inSourceWorkspace(notification.workspaceID, sourceWorkspaceIDs)
      ).length,
      marketplaceListings: skippedListings,
      sales: skippedSales
    }
  };
}

export function databaseFromBackup(backup: unknown): ReOSDatabase {
  const candidate = isRecord(backup) && isRecord(backup.data) ? backup.data : backup;
  if (!isRecord(candidate)) {
    throw new Error("Choose a Listwork backup file.");
  }

  return normalizeDatabase({
    currentUserID: hasText(candidate.currentUserID) ? candidate.currentUserID : undefined,
    activeWorkspaceID: hasText(candidate.activeWorkspaceID) ? candidate.activeWorkspaceID : undefined,
    users: arrayOrEmpty(candidate.users),
    workspaces: arrayOrEmpty(candidate.workspaces),
    workspaceMembers: arrayOrEmpty(candidate.workspaceMembers),
    subscriptions: arrayOrEmpty(candidate.subscriptions),
    entitlements: arrayOrEmpty(candidate.entitlements),
    inventoryItems: arrayOrEmpty(candidate.inventoryItems),
    itemPhotos: arrayOrEmpty(candidate.itemPhotos),
    storedAssets: arrayOrEmpty(candidate.storedAssets),
    marketplaceAccounts: arrayOrEmpty(candidate.marketplaceAccounts),
    marketplaceListings: arrayOrEmpty(candidate.marketplaceListings),
    sales: arrayOrEmpty(candidate.sales),
    notifications: arrayOrEmpty(candidate.notifications),
    categories: arrayOrEmpty(candidate.categories)
  } as Partial<ReOSDatabase>);
}

function workspaceIDsForSource(database: ReOSDatabase) {
  if (database.activeWorkspaceID) {
    return new Set([database.activeWorkspaceID]);
  }

  const workspaceIDs = database.workspaces.filter((workspace) => isRecord(workspace) && hasText(workspace.id)).map((workspace) => workspace.id);
  if (workspaceIDs.length > 0) {
    return new Set(workspaceIDs);
  }

  return new Set(
    database.inventoryItems
      .filter((item) => isRecord(item) && hasText(item.workspaceID))
      .map((item) => item.workspaceID)
  );
}

function inSourceWorkspace(workspaceID: unknown, sourceWorkspaceIDs: Set<string>) {
  return sourceWorkspaceIDs.size === 0 || (typeof workspaceID === "string" && sourceWorkspaceIDs.has(workspaceID));
}

function uniqueSKU(input: string, usedSKUs: Set<string>) {
  const base = input.trim() || "IMPORTED";
  let candidate = base;
  let suffix = 2;
  while (usedSKUs.has(candidate.toLowerCase())) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }
  usedSKUs.add(candidate.toLowerCase());
  return candidate;
}

function categoryNameForPath(path: string) {
  const parts = path.split("/").map((part) => part.trim()).filter(Boolean);
  return parts[parts.length - 1] ?? path;
}

function arrayOrEmpty(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function clone<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}
